import React, { useState } from 'react';
import { X, Mail, Plus, Trash2, ToggleLeft, ToggleRight, Send, Loader as Loader2, CircleCheck as CheckCircle, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import type { NotificationEmail } from '../../hooks/useSalesHistory';

interface NotificationEmailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  emails: NotificationEmail[];
  onAdd: (email: string, label?: string) => Promise<boolean>;
  onRemove: (id: string) => Promise<boolean>;
  onToggle: (id: string, isActive: boolean) => Promise<boolean>;
  onSetFormat: (id: string, format: 'excel' | 'pdf') => Promise<boolean>;
  onSendTest: () => Promise<{ success: boolean; message: string }>;
}

const MAX_EMAILS = 10;

export const NotificationEmailsModal: React.FC<NotificationEmailsModalProps> = ({
  isOpen,
  onClose,
  emails,
  onAdd,
  onRemove,
  onToggle,
  onSetFormat,
  onSendTest,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  if (!isOpen) return null;

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleAdd = async () => {
    if (!newEmail.trim() || !isValidEmail(newEmail.trim())) return;
    if (emails.length >= MAX_EMAILS) return;

    setAdding(true);
    const success = await onAdd(newEmail.trim(), newLabel.trim() || undefined);
    if (success) {
      setNewEmail('');
      setNewLabel('');
    }
    setAdding(false);
  };

  const handleRemove = async (id: string) => {
    setRemovingId(id);
    await onRemove(id);
    setRemovingId(null);
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    setTestResult(null);
    const result = await onSendTest();
    setTestResult(result);
    setSendingTest(false);
    setTimeout(() => setTestResult(null), 5000);
  };

  const activeCount = emails.filter(e => e.is_active).length;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Email Recipients
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {emails.length} of {MAX_EMAILS} configured
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {emails.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Add at least one email address to receive pick list notifications
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {emails.map(email => (
                <div
                  key={email.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                    email.is_active
                      ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50'
                      : 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 opacity-60'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      onSetFormat(
                        email.id,
                        (email.report_format || 'excel') === 'excel' ? 'pdf' : 'excel'
                      )
                    }
                    title={
                      (email.report_format || 'excel') === 'excel'
                        ? 'Format: Excel (.xlsx) — click for PDF'
                        : 'Format: PDF — click for Excel'
                    }
                    aria-label={
                      (email.report_format || 'excel') === 'excel'
                        ? 'Attachment format Excel, switch to PDF'
                        : 'Attachment format PDF, switch to Excel'
                    }
                    className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase transition-colors ${
                      (email.report_format || 'excel') === 'excel'
                        ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                        : 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
                    }`}
                  >
                    {(email.report_format || 'excel') === 'excel' ? 'XLSX' : 'PDF'}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      email.is_active
                        ? 'text-gray-900 dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {email.email}
                    </p>
                    {email.label && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {email.label}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => onToggle(email.id, !email.is_active)}
                    className="flex-shrink-0 focus:outline-none"
                  >
                    {email.is_active ? (
                      <ToggleRight className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                    ) : (
                      <ToggleLeft className="h-6 w-6 text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => handleRemove(email.id)}
                    disabled={removingId === email.id}
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {removingId === email.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {emails.length < MAX_EMAILS && (
            <div className="border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl p-4 space-y-3">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  onKeyDown={e => e.key === 'Enter' && handleAdd()}
                />
                <Button
                  onClick={handleAdd}
                  variant="primary"
                  size="sm"
                  loading={adding}
                  disabled={!newEmail.trim() || !isValidEmail(newEmail.trim())}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <input
                type="text"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                placeholder="Label (optional, e.g. Store Manager)"
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          )}

          {testResult && (
            <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
              testResult.success
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            }`}>
              {testResult.success ? (
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
              ) : (
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              )}
              {testResult.message}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <Button
            onClick={handleSendTest}
            variant="secondary"
            size="sm"
            loading={sendingTest}
            disabled={activeCount === 0}
          >
            <Send className="h-4 w-4 mr-1.5" />
            Send Test Email
          </Button>
          <Button onClick={onClose} variant="primary">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};
